import { useState, useEffect, useRef, useCallback } from 'react';

const KRAKEN_URL = 'https://api.kraken.com/0/public/Ticker?pair=ETHUSD';
const CACHE_MS = 30_000; // 30 seconds

let cachedPrice: number | null = null;
let cachedAt: number = 0;

async function fetchEthUsd(): Promise<number> {
  const res = await fetch(KRAKEN_URL);
  const data = await res.json();
  if (data.error && data.error.length > 0) throw new Error(data.error.join(', '));
  const pair = data.result?.XETHZUSD ?? data.result?.ETHUSD;
  if (!pair) throw new Error('No ETH/USD pair in response');
  const price = parseFloat(pair.c?.[0] ?? pair.c ?? '0');
  if (!price || isNaN(price)) throw new Error('Invalid price');
  return price;
}

export function useEthPrice() {
  const [price, setPrice] = useState<number | null>(() => cachedPrice);
  const [fetchedAt, setFetchedAt] = useState<number | null>(() => (cachedAt ? cachedAt : null));
  const [error, setError] = useState(false);
  const mounted = useRef(true);

  const fetchPrice = useCallback(async () => {
    const now = Date.now();
    if (cachedPrice != null && now - cachedAt < CACHE_MS) {
      setPrice(cachedPrice);
      setFetchedAt(cachedAt);
      setError(false);
      return;
    }
    try {
      const p = await fetchEthUsd();
      if (mounted.current) {
        cachedPrice = p;
        cachedAt = Date.now();
        setPrice(p);
        setFetchedAt(cachedAt);
        setError(false);
      }
    } catch (e) {
      if (mounted.current) {
        setError(true);
        setPrice(null);
        setFetchedAt(null);
      }
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    fetchPrice();
    const id = setInterval(fetchPrice, CACHE_MS);
    return () => {
      mounted.current = false;
      clearInterval(id);
    };
  }, [fetchPrice]);

  return { ethUsdPrice: price, fetchedAt, error };
}
